import React, { useMemo, useState } from 'react'
import { Button, Container, Toast, TypoGraph } from 'components'
import {
  Alert,
  Avatar,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SearchIcon from '@mui/icons-material/Search'
import { bulkUpdateFleets } from 'api'
import { IFleetData, IRecipientCandidate, IValidationTypeData } from 'interfaces/IFleetMonitoring'
import strings from 'strings'
import { isAdmin, isViewOnly } from 'tools/auth'
import { SetupState } from '..'
import OverrideInfoNote from '../overrideInfoNote'
import { recipientColor, recipientInitials, recipientLabel } from '../recipientDisplay'
import BulkOverrideModal from './bulkOverrideModal'
import BulkRecipientsModal from './bulkRecipientsModal'
import FleetDetail from './fleetDetail'
import styles from './fleetsTab.module.scss'

const FM = strings.FLEET_MONITORING

type FilterKey = 'all' | 'mine' | 'unassigned' | 'enrolled' | 'unenrolled'

type SortField = 'name' | 'cuid' | 'policyId' | 'owners' | 'alerts' | 'status'

interface IFleetsTabProps {
  fleets: IFleetData[]
  validationTypes: IValidationTypeData[]
  candidates: IRecipientCandidate[]
  onReload: () => Promise<void> | void
  setupState: SetupState
  onGoToSettings: () => void
  refreshing?: boolean
  dismissedBanner: SetupState | null
  onDismissBanner: () => void
}

const SETUP_BANNERS: Record<
  Exclude<SetupState, 'ok'>,
  { message: string; cta: string; severity: 'warning' | 'info' }
> = {
  skipped: {
    message: FM.FLEETS.SETUP_BANNER.SKIPPED,
    cta: FM.FLEETS.SETUP_BANNER.FINISH_SETUP,
    severity: 'warning',
  },
  paused: {
    message: FM.FLEETS.SETUP_BANNER.PAUSED,
    cta: FM.FLEETS.SETUP_BANNER.GO_TO_SETTINGS,
    severity: 'info',
  },
  no_alerts_enabled: {
    message: FM.FLEETS.SETUP_BANNER.NO_ALERTS_ENABLED,
    cta: FM.FLEETS.SETUP_BANNER.ENABLE_ALERTS,
    severity: 'warning',
  },
}

const FleetsTab: React.FunctionComponent<IFleetsTabProps> = ({
  fleets,
  validationTypes,
  candidates,
  onReload,
  setupState,
  onGoToSettings,
  refreshing,
  dismissedBanner,
  onDismissBanner,
}) => {
  const banner =
    refreshing || setupState === 'ok' || setupState === dismissedBanner
      ? null
      : SETUP_BANNERS[setupState]
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<FilterKey>('all')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [bulkModal, setBulkModal] = useState<null | 'override' | 'recipients'>(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [sort, setSort] = useState<{ field: SortField | null; dir: 'asc' | 'desc' }>({
    field: null,
    dir: 'asc',
  })
  const readOnly = isViewOnly()
  const canSelfAdd = isAdmin()
  const currentUserId = localStorage.getItem('userId')

  const loadFleets = onReload

  const counts = useMemo(() => {
    const enrolled = fleets.filter(f => f.enrolled).length
    return {
      all: fleets.length,
      enrolled,
      unenrolled: fleets.length - enrolled,
    }
  }, [fleets])

  const filteredFleets = useMemo(() => {
    const q = query.trim().toLowerCase()
    return fleets.filter(f => {
      if (filter === 'enrolled' && !f.enrolled) return false
      if (filter === 'unenrolled' && f.enrolled) return false
      if (filter === 'mine' && !(currentUserId && (f.recipients || []).includes(currentUserId)))
        return false
      if (filter === 'unassigned' && (f.recipients || []).length > 0) return false
      if (!q) return true
      return (
        f.name?.toLowerCase().includes(q) ||
        f.dot?.toLowerCase().includes(q) ||
        f.cuid?.toLowerCase().includes(q) ||
        f.policyId?.toLowerCase().includes(q)
      )
    })
  }, [fleets, query, filter, currentUserId])

  const sortedFleets = useMemo(() => {
    const field = sort.field
    if (!field) return filteredFleets
    const dir = sort.dir === 'asc' ? 1 : -1
    const sortValue = (f: IFleetData): string | number => {
      switch (field) {
        case 'name': return (f.name || '').toLowerCase()
        case 'cuid': return (f.cuid || '').toLowerCase()
        case 'policyId': return (f.policyId || '').toLowerCase()
        case 'owners': return (f.recipients || []).length
        case 'alerts': return f.activeAlertsCount || 0
        case 'status': return f.enrolled ? 1 : 0
        default: return ''
      }
    }
    return [...filteredFleets].sort((a, b) => {
      const av = sortValue(a)
      const bv = sortValue(b)
      if (av < bv) return -dir
      if (av > bv) return dir
      return 0
    })
  }, [filteredFleets, sort])

  const ownerStats = useMemo(() => {
    const total = fleets.length
    const assigned = fleets.filter(f => (f.recipients || []).length > 0).length
    const mine = currentUserId
      ? fleets.filter(f => (f.recipients || []).includes(currentUserId)).length
      : 0
    return { total, assigned, mine, unassigned: total - assigned }
  }, [fleets, currentUserId])

  const toggleSort = (field: SortField) =>
    setSort(prev => {
      if (prev.field !== field) return { field, dir: 'asc' }
      if (prev.dir === 'asc') return { field, dir: 'desc' }
      return { field: null, dir: 'asc' }
    })

  const visibleCarrierIds = useMemo(() => filteredFleets.map(f => f.carrierId), [filteredFleets])
  const allVisibleSelected =
    visibleCarrierIds.length > 0 && visibleCarrierIds.every(id => selected.has(id))
  const someVisibleSelected =
    visibleCarrierIds.some(id => selected.has(id)) && !allVisibleSelected

  const toggleSelect = (carrierId: number) =>
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(carrierId)) next.delete(carrierId)
      else next.add(carrierId)
      return next
    })

  const toggleSelectAllVisible = () =>
    setSelected(prev => {
      const next = new Set(prev)
      if (allVisibleSelected) visibleCarrierIds.forEach(id => next.delete(id))
      else visibleCarrierIds.forEach(id => next.add(id))
      return next
    })

  const clearSelection = () => setSelected(new Set())

  const addSelf = async (carrierId: number) => {
    if (!currentUserId) return
    setBulkBusy(true)
    try {
      await bulkUpdateFleets({
        carrierIds: [carrierId],
        operation: 'appendRecipients',
        payload: { recipients: [currentUserId] },
      })
      await loadFleets()
    } catch (e: any) {
      Toast({ type: 'error', subTitle: e?.message || FM.ERRORS.UPDATE_RECIPIENTS_FAILED })
    } finally {
      setBulkBusy(false)
    }
  }

  const toggleRow = (rowId: number) =>
    setExpandedRowId(prev => (prev === rowId ? null : rowId))

  const selectFilter = (next: FilterKey) => {
    setFilter(next)
    setExpandedRowId(null)
  }

  const selectedCarrierIds = Array.from(selected)

  const bulkEnrollment = async (enroll: boolean) => {
    if (selectedCarrierIds.length === 0) return
    setBulkBusy(true)
    try {
      await bulkUpdateFleets({
        carrierIds: selectedCarrierIds,
        operation: enroll ? 'enrollFleets' : 'unenrollFleets',
        payload: {},
      })
      Toast({
        type: 'success',
        subTitle: FM.TOASTS.FLEETS_ENROLLMENT_UPDATED(selectedCarrierIds.length, enroll),
      })
      await loadFleets()
      clearSelection()
    } catch (e: any) {
      Toast({ type: 'error', subTitle: e?.message || FM.ERRORS.UPDATE_ENROLLMENT_FAILED })
    } finally {
      setBulkBusy(false)
    }
  }

  const renderSortHeader = (field: SortField, label: string) => (
    <button type="button" className={styles.sortHeader} onClick={() => toggleSort(field)}>
      {label}
      <span className={`${styles.sortArrow} ${sort.field === field ? styles.sortArrowActive : ''}`}>
        {sort.field === field ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    </button>
  )

  const renderBody = () => {
    if (refreshing) {
      return (
        <Container className={styles.loader}>
          <CircularProgress />
        </Container>
      )
    }

    if (fleets.length === 0) {
      return (
        <div className={styles.empty}>
          <TypoGraph variant="body1" content={FM.FLEETS.EMPTY_NO_FLEETS} />
        </div>
      )
    }

    if (filteredFleets.length === 0) {
      return (
        <div className={styles.empty}>
          <TypoGraph variant="body1" content={FM.FLEETS.EMPTY_NO_MATCH} />
        </div>
      )
    }

    return (
      <>
        <div className={styles.columnHeaders}>
          <div className={styles.checkboxCell}>
            {!readOnly && (
              <Checkbox
                size="small"
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected}
                onChange={toggleSelectAllVisible}
                inputProps={{ 'aria-label': 'select all visible fleets' }}
              />
            )}
          </div>
          <div>{renderSortHeader('name', FM.FLEETS.COLUMN_FLEET)}</div>
          <div>{renderSortHeader('cuid', FM.FLEETS.COLUMN_CUID)}</div>
          <div>{renderSortHeader('policyId', FM.FLEETS.COLUMN_POLICY_ID)}</div>
          <div>{renderSortHeader('owners', FM.FLEETS.COLUMN_RECIPIENTS)}</div>
          <div>{renderSortHeader('alerts', FM.FLEETS.COLUMN_ALERTS)}</div>
          <div>{renderSortHeader('status', FM.FLEETS.COLUMN_STATUS)}</div>
          <div />
        </div>
        <div className={styles.rowsScroll}>
          {sortedFleets.map(fleet => (
            <FleetRow
              key={fleet.rowId}
              fleet={fleet}
              validationTypes={validationTypes}
              candidates={candidates}
              expanded={expandedRowId === fleet.rowId}
              selected={selected.has(fleet.carrierId)}
              readOnly={readOnly}
              currentUserId={currentUserId}
              canSelfAdd={canSelfAdd}
              bulkBusy={bulkBusy}
              onAddSelf={() => addSelf(fleet.carrierId)}
              onToggle={() => toggleRow(fleet.rowId)}
              onToggleSelect={() => toggleSelect(fleet.carrierId)}
              onUpdated={loadFleets}
            />
          ))}
        </div>
      </>
    )
  }

  return (
    <div data-testid="fleet-monitoring-fleets-body">
      <TypoGraph
        variant="body2"
        content={FM.FLEETS.DESCRIPTION}
        className={styles.tabDescription}
      />
      <OverrideInfoNote />
      {banner && (
        <Alert
          severity={banner.severity}
          sx={{
            marginBottom: '1rem',
            paddingTop: '14px',
            paddingBottom: '14px',
            alignItems: 'center',
            '& .MuiAlert-action': {
              alignItems: 'center',
              padding: 0,
              marginRight: 0,
              gap: '8px',
            },
          }}
          action={
            <>
              <Button
                label={banner.cta}
                variant="outlined"
                color="inherit"
                onClick={onGoToSettings}
              />
              <IconButton
                aria-label="dismiss"
                color="inherit"
                size="small"
                onClick={onDismissBanner}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
        >
          {banner.message}
        </Alert>
      )}
      {fleets.length > 0 && (
        <div className={styles.statCards}>
          <div className={`${styles.statCard} ${styles.statCardAssigned}`}>
            <div className={styles.statValue} data-testid="stat-assigned">
              {ownerStats.assigned}
              <span className={styles.statTotal}> / {ownerStats.total}</span>
            </div>
            <div className={styles.statLabel}>{FM.FLEETS.STAT_ASSIGNED}</div>
          </div>
          <div className={`${styles.statCard} ${styles.statCardMine}`}>
            <div className={styles.statValue} data-testid="stat-mine">
              {ownerStats.mine}
              <span className={styles.statTotal}> / {ownerStats.total}</span>
            </div>
            <div className={styles.statLabel}>{FM.FLEETS.STAT_MINE}</div>
          </div>
          <div className={`${styles.statCard} ${styles.statCardUnassigned}`}>
            <div className={styles.statValue} data-testid="stat-unassigned">
              {ownerStats.unassigned}
              <span className={styles.statTotal}> / {ownerStats.total}</span>
            </div>
            <div className={styles.statLabel}>{FM.FLEETS.STAT_UNASSIGNED}</div>
          </div>
        </div>
      )}
      <Paper elevation={0} sx={{ border: '1px solid #E9E9E9', borderRadius: '8px', overflow: 'hidden' }}>
      <div className={styles.toolbar}>
        <TextField
          size="small"
          placeholder={FM.FLEETS.SEARCH_PLACEHOLDER}
          value={query}
          onChange={e => setQuery(e.target.value)}
          className={styles.search}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <div className={styles.filterChips}>
          <FilterChip
            label={FM.FLEETS.FILTER_ALL}
            count={counts.all}
            active={filter === 'all'}
            onClick={() => selectFilter('all')}
          />
          <FilterChip
            label={FM.FLEETS.FILTER_MINE}
            count={ownerStats.mine}
            active={filter === 'mine'}
            onClick={() => selectFilter('mine')}
          />
          <FilterChip
            label={FM.FLEETS.FILTER_UNASSIGNED}
            count={ownerStats.unassigned}
            active={filter === 'unassigned'}
            onClick={() => selectFilter('unassigned')}
          />
          <span className={styles.filterDivider} />
          <FilterChip
            label={FM.FLEETS.FILTER_ENROLLED}
            count={counts.enrolled}
            active={filter === 'enrolled'}
            onClick={() => selectFilter('enrolled')}
          />
          <FilterChip
            label={FM.FLEETS.FILTER_UNENROLLED}
            count={counts.unenrolled}
            active={filter === 'unenrolled'}
            onClick={() => selectFilter('unenrolled')}
          />
        </div>
      </div>

      {selected.size > 0 && (
        <div className={styles.bulkBar}>
          <span className={styles.bulkBarCount}>{FM.FLEETS.SELECTED_COUNT(selected.size)}</span>
          <div className={styles.bulkBarActions}>
            <Button
              label={FM.FLEETS.SET_OVERRIDE}
              variant="outlined"
              onClick={() => setBulkModal('override')}
              disabled={bulkBusy}
            />
            <Button
              label={FM.FLEETS.SET_RECIPIENTS}
              variant="outlined"
              onClick={() => setBulkModal('recipients')}
              disabled={bulkBusy}
            />
            <Button
              label={FM.FLEETS.ENROLL}
              variant="outlined"
              onClick={() => bulkEnrollment(true)}
              disabled={bulkBusy}
            />
            <Button
              label={FM.FLEETS.UNENROLL}
              variant="outlined"
              onClick={() => bulkEnrollment(false)}
              disabled={bulkBusy}
            />
            <Button label={FM.FLEETS.CLEAR} variant="text" onClick={clearSelection} disabled={bulkBusy} />
          </div>
        </div>
      )}

      {renderBody()}

      <BulkOverrideModal
        open={bulkModal === 'override'}
        carrierIds={selectedCarrierIds}
        validationTypes={validationTypes}
        onClose={() => setBulkModal(null)}
        onApplied={async () => {
          await loadFleets()
          clearSelection()
        }}
      />
      <BulkRecipientsModal
        open={bulkModal === 'recipients'}
        carrierIds={selectedCarrierIds}
        validationTypes={validationTypes}
        candidates={candidates}
        onClose={() => setBulkModal(null)}
        onApplied={async () => {
          await loadFleets()
          clearSelection()
        }}
      />
      </Paper>
    </div>
  )
}

interface IFilterChipProps {
  label: string
  count: number
  active: boolean
  onClick: () => void
}

const FilterChip: React.FC<IFilterChipProps> = ({ label, count, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${styles.filterChip} ${active ? styles.filterChipActive : ''}`}
  >
    {label}
    <span className={styles.filterChipCount}>{count}</span>
  </button>
)

interface IFleetRowProps {
  fleet: IFleetData
  validationTypes: IValidationTypeData[]
  candidates: IRecipientCandidate[]
  expanded: boolean
  selected: boolean
  readOnly: boolean
  currentUserId: string | null
  canSelfAdd: boolean
  bulkBusy: boolean
  onAddSelf: () => void
  onToggle: () => void
  onToggleSelect: () => void
  onUpdated: () => void | Promise<unknown>
}

const FleetRow: React.FC<IFleetRowProps> = ({
  fleet,
  validationTypes,
  candidates,
  expanded,
  selected,
  readOnly,
  currentUserId,
  canSelfAdd,
  bulkBusy,
  onAddSelf,
  onToggle,
  onToggleSelect,
  onUpdated,
}) => {
  const recipients = fleet.recipients || []
  const hasAlerts = (fleet.activeAlertsCount || 0) > 0
  const byId = new Map(candidates.map(c => [c.userId, c]))
  const owners = recipients
    .map(id => byId.get(id))
    .filter((c): c is IRecipientCandidate => !!c)
  const isOwner = !!currentUserId && recipients.includes(currentUserId)

  return (
    <div className={styles.rowWrapper}>
      <div
        className={`${styles.tableRow} ${expanded ? styles.tableRowExpanded : ''} ${selected ? styles.tableRowSelected : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
      >
        <div
          className={styles.checkboxCell}
          onClick={e => e.stopPropagation()}
        >
          {!readOnly && (
            <Checkbox
              size="small"
              checked={selected}
              onChange={onToggleSelect}
              inputProps={{ 'aria-label': `select ${fleet.name}` }}
            />
          )}
        </div>

        <div>
          <div className={styles.fleetNameRow}>
            <Tooltip title={fleet.name} placement="top-start" enterDelay={400}>
              <div className={styles.fleetName}>{fleet.name}</div>
            </Tooltip>
            {isOwner && <span className={styles.yoursBadge}>{FM.FLEETS.YOURS}</span>}
          </div>
          {fleet.dot && <div className={styles.fleetSubLabel}>{FM.FLEETS.DOT_PREFIX} {fleet.dot}</div>}
        </div>

        <div className={styles.mono}>{fleet.cuid || '—'}</div>

        <div className={styles.mono}>{fleet.policyId || '—'}</div>

        <div className={styles.recipientsCell} onClick={e => e.stopPropagation()}>
          {owners.length === 0 ? (
            <span className={styles.recipient}>—</span>
          ) : (
            <>
              {owners.slice(0, 2).map(owner => (
                <span key={owner.userId} className={styles.ownerItem} title={recipientLabel(owner)}>
                  <Avatar
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: 10,
                      bgcolor: recipientColor(owner.userId),
                      color: '#fff',
                    }}
                  >
                    {recipientInitials(owner)}
                  </Avatar>
                  <span className={styles.ownerName}>{recipientLabel(owner)}</span>
                </span>
              ))}
              {owners.length > 2 && (
                <span className={styles.recipientMore}>
                  {FM.FLEETS.RECIPIENTS_MORE(owners.length - 2)}
                </span>
              )}
            </>
          )}
          {canSelfAdd && !isOwner && currentUserId && (
            <button
              type="button"
              className={styles.addMeBtn}
              onClick={onAddSelf}
              disabled={bulkBusy}
            >
              {FM.FLEETS.ADD_ME}
            </button>
          )}
        </div>

        <div>
          <span className={`${styles.alertCount} ${hasAlerts ? styles.alertCountActive : ''}`}>
            {fleet.activeAlertsCount ?? 0}
          </span>
        </div>

        <div>
          <span
            className={`${styles.statusPill} ${
              fleet.enrolled ? styles.statusPillEnrolled : styles.statusPillUnenrolled
            }`}
          >
            <span className={styles.statusDot} />
            {fleet.enrolled ? FM.FLEETS.STATUS_ENROLLED : FM.FLEETS.STATUS_NOT_ENROLLED}
          </span>
        </div>

        <div className={`${styles.chevron} ${expanded ? styles.chevronExpanded : ''}`}>
          <KeyboardArrowDownIcon fontSize="small" />
        </div>
      </div>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={styles.expandedRow}>
          <FleetDetail
            fleet={fleet}
            validationTypes={validationTypes}
            candidates={candidates}
            onUpdated={onUpdated}
          />
        </div>
      </Collapse>
    </div>
  )
}

export default FleetsTab
